// F-U exhibition period helpers (2026-10-05).
//
// The wire carries calendar dates as ISO "YYYY-MM-DD" (no timezone — a
// period is a local date range). Staff see and type "YYYY/MM/DD".

const DISPLAY_RE = /^(\d{4})[/-](\d{2})[/-](\d{2})$/;

/** ISO "2026-03-01" → "2026/03/01"; null / "" → "". */
export function formatPeriodDate(iso: string | null | undefined): string {
  return iso ? iso.replace(/-/g, "/") : "";
}

/** "2026/03/01" (or "2026-03-01") → ISO "2026-03-01" when it is a real
 *  calendar date; otherwise null. */
export function parsePeriodDate(text: string): string | null {
  const m = DISPLAY_RE.exec(text.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  const valid =
    dt.getUTCFullYear() === Number(y) &&
    dt.getUTCMonth() === Number(mo) - 1 &&
    dt.getUTCDate() === Number(d);
  return valid ? `${y}-${mo}-${d}` : null;
}

/** Typing aid: keep digits only and insert the slashes — "20260301" →
 *  "2026/03/01". Caps at 8 digits. */
export function maskPeriodInput(text: string): string {
  const digits = text.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}/${digits.slice(4)}`;
  return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`;
}

/** Form-level rule, mirroring the backend: both-or-neither, each a real
 *  date, end on/after start. Returns the message to show, or null. */
export function periodProblem(
  startText: string,
  endText: string,
): string | null {
  const s = startText.trim();
  const e = endText.trim();
  if (!s && !e) return null;
  if (!s || !e)
    return "Enter both a start and an end date, or leave both empty";
  const start = parsePeriodDate(s);
  const end = parsePeriodDate(e);
  if (!start) return "Start date must be a real date in YYYY/MM/DD";
  if (!end) return "End date must be a real date in YYYY/MM/DD";
  if (end < start) return "End date must be on or after start date";
  return null;
}

/** "2026/03/01 – 2026/05/31", or "" when no period is set. */
export function formatPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return "";
  return `${formatPeriodDate(start)} – ${formatPeriodDate(end)}`;
}

/** Every calendar year the period touches (overlap rule) — e.g.
 *  2025-12-01 → 2026-02-28 gives [2025, 2026]. Empty when no period. */
export function periodYears(
  start: string | null | undefined,
  end: string | null | undefined,
): number[] {
  if (!start || !end) return [];
  const from = Number(start.slice(0, 4));
  const to = Number(end.slice(0, 4));
  const years: number[] = [];
  for (let y = from; y <= to; y += 1) years.push(y);
  return years;
}
